import { MutableRefObject, useCallback, useEffect, useLayoutEffect, useMemo } from 'react';
import List from '@components/List';
import { useDispatch, useSelector } from 'react-redux';
import { resetScrollPosition, saveScrollPosition, selectScrollPosition } from '@actions/ui';

const elementGetter = (
  scrollingContainerRef?: MutableRefObject<HTMLElement>,
  listRef?: MutableRefObject<List<any>>,
): (() => HTMLElement) => {
  return () => {
    if (scrollingContainerRef) {
      return scrollingContainerRef.current;
    } else if (listRef) {
      return listRef.current?.container;
    }
  };
};

/*
This can be used with a regular DOM ref for any nested scrolling container
in the UI. This includes any `ref` forwarded to our <VirtualizedList/>. Refs
forwarded to our  <List/> component (not virtualized, @components/List) set
a different interface on the ref, where the scrolling container is nested in
ref.current (hence the elementGetter fn).
*/
export const useScrollRestoration = ({
  viewID,
  scrollingContainerRef,
  listRef,
}: {
  viewID: string;
  scrollingContainerRef?: MutableRefObject<HTMLElement>;
  listRef?: MutableRefObject<List<any>>;
}): boolean => {
  const dispatch = useDispatch();
  const scrollPosition = useSelector(selectScrollPosition(viewID));
  const getElement = useCallback(elementGetter(scrollingContainerRef, listRef), []);
  const isScrolledAtMount = useMemo(() => scrollPosition && scrollPosition > 0, []);

  useEffect(() => {
    if (!!scrollPosition) {
      setTimeout(() => getElement()?.scrollTo(0, scrollPosition), 0);
      dispatch(resetScrollPosition(viewID));
    }
  }, []);

  useLayoutEffect(
    () => () => {
      dispatch(saveScrollPosition({ viewID, scrollPosition: getElement()?.scrollTop || 0 }));
    },
    [],
  );

  return isScrolledAtMount;
};
